import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';

import { FeatureGateService } from 'src/shared/services/feature-gate.service';
import { LeaseService, Lease, LedgerSummary, LedgerEntry } from 'src/shared/services/lease.service';
import { TenantPortalService } from 'src/shared/services/tenant-portal.service';

// Precomputed per-entry view model for the ledger card list — keeps the template
// free of function calls (CLAUDE.md rule).
interface LedgerEntryView {
  id: number;
  description: string;
  amountLabel: string;
  amountColor: string;
  metaLabel: string;
  balanceLabel: string;
}

@Component({
  templateUrl: 'lease-ledger.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LeaseLedgerPage implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private util = inject(Util);
  private featureGate = inject(FeatureGateService);
  private leaseService = inject(LeaseService);
  private portalService = inject(TenantPortalService);

  readonly windowService = inject(WindowService);

  isRightSideContent = true;
  leaseId: number;
  lease: Lease;
  summary: LedgerSummary;
  ledgerViews: LedgerEntryView[] = [];
  loaded = false;
  private panelClosedSubscription: Subscription;

  // Precomputed display strings for room/cadence details — no function calls in the template.
  propertyLabel = '';
  rentSummaryLabel = '';
  cadenceLabel = '';
  termTypeLabel = '';
  cadenceTermLabel = '';
  rentDetailLabel = '';
  liabilityLabel = '';
  statusTextColor = '';
  balanceText = '';
  balanceTextColor = '';

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    const params = await this.storage.retrieve('dialog-params');
    const lease = this.navCtrl.getParam('lease') || params?.lease;
    this.leaseId = lease?.id ?? this.navCtrl.getParam('leaseId') ?? params?.leaseId;
    await this.refresh();
    if (this.isRightSideContent && this.lease) {
      this.rightSidePanelService.setDialogPageTitle(this.lease.tenant_name);
    }
    setTimeout(() => {
      this.loaded = true;
      this.cdr.markForCheck();
    });
  }

  async refresh(): Promise<void> {
    this.lease = await this.leaseService.getLease(this.leaseId);
    if (!this.lease) { return; }
    this.summary = this.leaseService.computeLedger(this.lease);
    this.ledgerViews = this.buildLedgerViews(this.summary.entries);
    this.buildLeaseLabels(this.lease);
    this.cdr.markForCheck();
  }

  // `unit` already equals the room label for room leases (e.g. "Room A"); only append
  // `space_name` when it adds new information, so whole-unit leases render unchanged.
  buildLeaseLabels(lease: Lease): void {
    const base = `${lease.property_name} · ${lease.unit}`;
    this.propertyLabel = lease.space_name && lease.space_name !== lease.unit ? `${base} · ${lease.space_name}` : base;

    this.rentSummaryLabel = lease.cadence === 'weekly' && lease.weekly_rent
      ? `$${lease.weekly_rent.toLocaleString('en-US')}/wk ($${lease.monthly_rent.toLocaleString('en-US')}/mo)`
      : `$${lease.monthly_rent.toLocaleString('en-US')}/mo`;

    this.cadenceLabel = lease.cadence === 'weekly' ? 'Weekly' : lease.cadence === 'monthly' ? 'Monthly' : '';
    this.termTypeLabel = lease.term_type === 'month_to_month' ? 'Month-to-month' : lease.term_type === 'fixed' ? 'Fixed term' : '';
    this.cadenceTermLabel = [this.cadenceLabel, this.termTypeLabel].filter(Boolean).join(' · ');
    this.rentDetailLabel = [this.rentSummaryLabel, this.cadenceTermLabel].filter(Boolean).join('  ·  ');
    this.liabilityLabel = lease.liability === 'individual' ? 'Individual liability' : lease.liability === 'joint' ? 'Joint liability' : '';

    this.statusTextColor = this.leaseService.statusColor(lease.status);
    const balance = this.summary?.balance || 0;
    this.balanceText = this.leaseService.balanceLabel(balance);
    this.balanceTextColor = this.leaseService.balanceColor(balance);
  }

  // Payments read as credits (green, negative); charges as debits.
  buildLedgerViews(entries: LedgerEntry[]): LedgerEntryView[] {
    return entries.map(e => ({
      id: e.id,
      description: e.description,
      amountLabel: e.payment
        ? `-$${e.payment.toLocaleString('en-US')}`
        : `$${(e.charge || 0).toLocaleString('en-US')}`,
      amountColor: e.payment ? '#2E7D32' : '#1E2125',
      metaLabel: `${new Date(e.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · ${e.type}`,
      balanceLabel: `Balance ${this.signed(e.balance || 0)}`,
    }));
  }

  signed(amount: number): string {
    if (amount < 0) { return `-$${  Math.abs(amount).toLocaleString('en-US')}`; }
    return `$${  amount.toLocaleString('en-US')}`;
  }

  async recordPayment(): Promise<void> {
    this.featureGate.gate();
  }

  async assessLateFee(): Promise<void> {
    this.featureGate.gate();
  }

  sendStatement(): void {
    this.featureGate.gate();
  }

  get portalLink(): string {
    return this.lease ? this.portalService.portalLink(this.lease.tenant_email || this.lease.tenant_name) : '';
  }

  copyPortalLink(): void {
    try { navigator?.clipboard?.writeText(this.portalLink); } catch { /* clipboard unavailable */ }
    this.util.showSuccess(`Tenant portal link copied: ${  this.portalLink}`);
  }

  goToPage(url: string, params: any, component: any) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      this.rightSidePanelService.navigateTo(url, params, component);
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => this.cdr.markForCheck());
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
